// Single Gantt row — the job's main bar, optional spool sub-rows,
// optional per-stage sub-bars beneath each spool. Stage bars colour
// from the canonical STAGES palette; the short code is rendered
// when the bar is wide enough to fit.
import type { ReactElement } from "react";
import type {
  JobDto,
  SpoolDto,
  SpoolStageDto,
  StageDefDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "./StagesProvider";
import {
  barBounds,
  isTodayUnit,
  todayPx,
  type GanttScale,
} from "../lib/ganttScale";
import { activeStages, spoolLatestFinish, stageFinish } from "../lib/stageHelpers";
import { STATUS_COLORS } from "./StatusBadge";
import { useStatusByLabel } from "./JobStatusesProvider";
import { AssigneeChip } from "./AssigneeChip";

// Spool bar bounds. Prefer the legacy cutStart/weldDone pair when
// the user filled them in; otherwise derive from stage data (earliest
// startDate → spoolLatestFinish). Falls back to the spool's dueDate
// or the parent job's dates so the row still renders something.
function spoolBarBounds(spool: SpoolDto, job: JobDto): { start: string; end: string } | null {
  if (spool.cutStart && spool.weldDone) {
    return { start: spool.cutStart, end: spool.weldDone };
  }
  const starts = activeStages(spool.stages)
    .map((s) => s.startDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  const earliest = starts[0] ?? spool.cutStart ?? job.startDate;
  const latest = spoolLatestFinish(spool.stages) ?? spool.dueDate ?? spool.weldDone ?? job.dueDate;
  if (!earliest || !latest) return null;
  return { start: earliest, end: latest < earliest ? earliest : latest };
}

const SPOOL_BAR_COLOR: Record<string, string> = {
  pending: "#9CA09F",
  cutting: "#C77700",
  cut: "#0284C7",
  welding: "#C77700",
  complete: "#2E7D32",
};

interface Props {
  job: JobDto;
  scale: GanttScale;
  showSpools: boolean;
  showStages: boolean;
  onOpen(id: number): void;
}

function cells(scale: GanttScale): ReactElement[] {
  const out: ReactElement[] = [];
  for (let i = 0; i < scale.totalUnits; i += 1) {
    out.push(
      <div
        key={i}
        className={`g-cell ${isTodayUnit(scale, i) ? "tc" : ""}`}
        style={{ width: scale.unitWidth }}
      />,
    );
  }
  return out;
}

// Job bar dates: use explicit job start/due when set; otherwise
// derive from spools (earliest spool start → latest spool finish)
// so a job with stage dates but no job-level dates still plots.
function jobBarBounds(job: JobDto): { start: string; end: string } | null {
  if (job.startDate && job.dueDate) {
    return { start: job.startDate, end: job.dueDate };
  }
  const starts: string[] = [];
  const ends: string[] = [];
  for (const sp of job.spools) {
    const b = spoolBarBounds(sp, job);
    if (b) {
      starts.push(b.start);
      ends.push(b.end);
    }
  }
  starts.sort();
  ends.sort();
  const start = job.startDate ?? starts[0];
  const end = job.dueDate ?? ends[ends.length - 1];
  if (!start || !end) return null;
  return { start, end: end < start ? start : end };
}

export function GanttRow({ job, scale, showSpools, showStages, onOpen }: Props) {
  const catalogue = useActiveStages();
  const statusLookup = useStatusByLabel();
  const bounds = jobBarBounds(job);
  const clipped = bounds ? barBounds(scale, bounds.start, bounds.end) : null;
  const tPx = todayPx(scale);
  return (
    <>
      <div className="g-row">
        <div className="g-row-label" onClick={() => onOpen(job.id)}>
          <AssigneeChip member={job.assignee} size={22} />
          <span style={{ fontWeight: 700 }}>{job.jobNumber}</span>
          <span style={{ fontSize: 11, color: "var(--bsp-fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {job.name}
          </span>
        </div>
        <div className="g-cells" style={{ width: scale.totalWidth }}>
          {cells(scale)}
          {clipped && (
            <div
              className="g-bar"
              style={{
                left: clipped.left,
                top: 8,
                height: 24,
                width: clipped.width,
                background: statusLookup(job.status)?.color ?? STATUS_COLORS[job.status] ?? "#1B449C",
                borderTopLeftRadius: clipped.clippedLeft ? 0 : undefined,
                borderBottomLeftRadius: clipped.clippedLeft ? 0 : undefined,
                borderTopRightRadius: clipped.clippedRight ? 0 : undefined,
                borderBottomRightRadius: clipped.clippedRight ? 0 : undefined,
              }}
              onClick={() => onOpen(job.id)}
              title={`${job.name} (${job.status})`}
            >
              {job.jobNumber} · {job.name}
            </div>
          )}
          {tPx !== null && <div className="today-ln" style={{ left: tPx }} />}
        </div>
      </div>
      {showSpools &&
        job.spools.map((s) => {
          const stageBarRow = showStages ? renderStageBars(s, scale, catalogue) : null;
          const bounds = spoolBarBounds(s, job);
          if (!bounds) {
            return stageBarRow ? (
              <div key={`s-${s.id}`}>{stageBarRow}</div>
            ) : null;
          }
          const sClipped = barBounds(scale, bounds.start, bounds.end, 12);
          return (
            <div key={s.id}>
              <div className="g-row" style={{ minHeight: 24 }}>
                <div
                  className="g-row-label"
                  style={{ fontSize: 10, paddingLeft: 32, color: "var(--bsp-fg-muted)" }}
                >
                  ↳ {s.num}
                </div>
                <div className="g-cells" style={{ width: scale.totalWidth }}>
                  {cells(scale)}
                  {sClipped && (
                    <div
                      className="g-bar spool-bar"
                      style={{
                        left: sClipped.left,
                        top: 4,
                        height: 14,
                        width: sClipped.width,
                        background: SPOOL_BAR_COLOR[s.status] ?? "#9CA09F",
                        borderTopLeftRadius: sClipped.clippedLeft ? 0 : undefined,
                        borderBottomLeftRadius: sClipped.clippedLeft ? 0 : undefined,
                        borderTopRightRadius: sClipped.clippedRight ? 0 : undefined,
                        borderBottomRightRadius: sClipped.clippedRight ? 0 : undefined,
                      }}
                      title={`${s.num} · ${bounds.start} → ${bounds.end}`}
                    >
                      {s.num}
                    </div>
                  )}
                </div>
              </div>
              {stageBarRow}
            </div>
          );
        })}
    </>
  );
}

// Stage bars stacked vertically by canonical STAGES order — one row
// per stage with a startDate. Pending stages render at their
// computed finish (startDate + ceil(hours/8)) so the cascade is
// visible before any stage is marked complete. Skipped stages are
// omitted so the row stays compact.
function renderStageBars(
  spool: SpoolDto,
  scale: GanttScale,
  catalogue: StageDefDto[],
): ReactElement | null {
  type DatedStage = SpoolStageDto & { startDate: string; finish: string };
  const dated: DatedStage[] = [];
  for (const st of spool.stages) {
    if (st.status === "skipped" || !st.startDate) continue;
    const finish = stageFinish(st);
    if (!finish) continue;
    dated.push({ ...st, startDate: st.startDate, finish });
  }
  if (dated.length === 0) return null;
  const order = new Map<string, number>();
  catalogue.forEach((d, i) => order.set(d.stageKey, i));
  const byKey = new Map<string, StageDefDto>();
  for (const d of catalogue) byKey.set(d.stageKey, d);
  dated.sort((a, b) => (order.get(a.stageKey) ?? 99) - (order.get(b.stageKey) ?? 99));
  return (
    <>
      {dated.map((st) => {
        const def = byKey.get(st.stageKey);
        const clipped = barBounds(scale, st.startDate, st.finish, 8);
        const isComplete = st.status === "complete";
        return (
          <div key={`${spool.id}-${st.stageKey}`} className="g-row" style={{ minHeight: 18 }}>
            <div
              className="g-row-label"
              style={{ fontSize: 9, paddingLeft: 52, color: "var(--bsp-fg-muted)" }}
            >
              {def?.short ?? st.stageKey}
            </div>
            <div className="g-cells" style={{ width: scale.totalWidth }}>
              {cells(scale)}
              {clipped && (
                <div
                  className="g-bar"
                  style={{
                    left: clipped.left,
                    top: 2,
                    height: 12,
                    width: clipped.width,
                    background: def?.color ?? "#9CA09F",
                    opacity: isComplete ? 1 : 0.78,
                    fontSize: 8,
                    paddingLeft: 4,
                    border: isComplete ? "1px solid rgba(0,0,0,0.15)" : undefined,
                    borderTopLeftRadius: clipped.clippedLeft ? 0 : undefined,
                    borderBottomLeftRadius: clipped.clippedLeft ? 0 : undefined,
                    borderTopRightRadius: clipped.clippedRight ? 0 : undefined,
                    borderBottomRightRadius: clipped.clippedRight ? 0 : undefined,
                  }}
                  title={`${spool.num} · ${def?.name ?? st.stageKey} · ${st.status} · ${st.hours}h · ${st.startDate} → ${st.finish}`}
                >
                  {clipped.width > 28 ? (def?.short ?? st.stageKey) : ""}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
